import { ComponentType, h } from "preact";
import { useRef, useState } from "preact/hooks";

import { DragDropTargetPosition } from "../../../foundation/enums";
import classname from "../../../foundation/utils/classname";
import { pipe } from "../../../foundation/utils/legacy";

import IconDragSmall from "../Icons/IconDragSmall";
import IconProps from "../Icons/IconProps";
import { DragPosition, DragSource, DragSourceProps, DropTarget } from "../../DragDropContainer";

import { useReorderListContext } from "./ReorderListContext";
import ReorderListData from "./ReorderListData";

import styles from "./ReorderList.module.css";

interface WithReorderListItemProps extends DragSourceProps {
    class?: string;
    id: string;
    index: number;
    mirror?: boolean;
}

type DragData = WithReorderListItemProps;

interface DragDropInjectedProps {
    onTargetHover(mirrorPosition: DragPosition): void;
    onTargetHoverOut(): void;
    onTargetDrop(dragData: DragData): void;
}

type RawDragDropReorderListItemProps = WithReorderListItemProps & DragDropInjectedProps;

type DragDropIconProps = IconProps & DragSourceProps;

function RawDragIcon(props: DragDropIconProps) {
    return (
        <div class={styles.dragIcon}>
            <IconDragSmall class={classname(styles.icon, props.class)}/>
        </div>
    );
}

function withReorderListItem<P extends WithReorderListItemProps>(
    WrappedComponent: ComponentType<P>,
    itemType: string
): ComponentType<P> {
    const RawDragDropReorderListItem: ComponentType<RawDragDropReorderListItemProps> = pipe(
        DragSource<RawDragDropReorderListItemProps>({
            itemType,
            mirrorComponent: (props: RawDragDropReorderListItemProps) => (
                <WrappedComponent mirror {...(props as unknown as P)}/>
            ),
            provideDragData: props => props
        }),
        DropTarget<RawDragDropReorderListItemProps, DragData>({
            acceptsItemType: [itemType, "dragIcon"],
            onHover: (props, dragState) => props.onTargetHover(dragState.mirrorPosition),
            onHoverOut: props => props.onTargetHoverOut(),
            onDrop: (props, dragState) => props.onTargetDrop(dragState.dragData!)
        })
    )(WrappedComponent);

    const DragDropIcon = DragSource<DragDropIconProps>({
        itemType: "dragIcon",
        mirrorComponent: (props: DragDropIconProps) => (
            <WrappedComponent mirror {...(props as unknown as P)}/>
        ),
        provideDragData: props => props
    })(RawDragIcon);

    return ({ dragBaseRef, ...props }: WithReorderListItemProps) => {
        const { showDragIcon, onReorder } = useReorderListContext();
        const baseRef = useRef<HTMLDivElement>(null);
        const [targetPosition, setTargetPosition] = useState<DragDropTargetPosition | null>(null);

        function handleDragBaseRef(el: HTMLDivElement) {
            baseRef.current = el;
            dragBaseRef?.(el);
        }

        function handleHover(mirrorPosition: DragPosition) {
            const baseRect = baseRef.current?.getBoundingClientRect();
            if (!baseRect) {
                return;
            }

            // eslint-disable-next-line no-magic-numbers
            const baseCenter = baseRect.top + (baseRect.height / 2);
            setTargetPosition(mirrorPosition.top > baseCenter
                ? DragDropTargetPosition.BOTTOM
                : DragDropTargetPosition.TOP
            );
        }

        function handleHoverOut() {
            setTargetPosition(null);
        }

        function handleDrop({ id, index: sourceIndex }: DragData) {
            const { index: targetIndex } = props;

            let moveIndex = targetIndex;
            if (sourceIndex > targetIndex && targetPosition === DragDropTargetPosition.BOTTOM) {
                moveIndex += 1;
            } else if (sourceIndex < targetIndex && targetPosition === DragDropTargetPosition.TOP) {
                moveIndex -= 1;
            }

            if (sourceIndex === moveIndex) {
                return;
            }

            onReorder({
                sourceId: id,
                sourceIndex,
                moveIndex
            });
        }

        const { class: cssClass, ...iconProps } = props;

        return (
            <div class={classname(styles.reorderListItem, cssClass, {
                [styles.draggingTop]: targetPosition === DragDropTargetPosition.TOP,
                [styles.draggingBottom]: targetPosition === DragDropTargetPosition.BOTTOM
            })}>
                {showDragIcon && (
                    <DragDropIcon {...(iconProps as P)}/>
                )}
                <div class={styles.body}>
                    <RawDragDropReorderListItem
                        {...(props as P)}
                        dragBaseRef={handleDragBaseRef}
                        onTargetHover={handleHover}
                        onTargetHoverOut={handleHoverOut}
                        onTargetDrop={handleDrop}/>
                </div>
            </div>
        );
    };
}

export default withReorderListItem;
export {
    WithReorderListItemProps,
    ReorderListData
};
